<script setup>
const {getCourseCatalogGrid, getCoursesGrid, getAgeRange } = useCourseData();
const {data: catalog} = await getCourseCatalogGrid();
const { extractGenres, extractPrograms, extractLearningStyles } = useCourseFilters();
const {data: coursesData, error} = await getCoursesGrid();
const {setPageHead} = useGlobalFeaturesData();
const {slugify} = useLocationData();
const route = useRoute()
const interest = useInterest();
const brochureRequestStore = useBrochureRequest();
const { isBootstrapLoaded } = useBootstrapLoaded()

const showCourseRec = computed(() => route.query.showCourseRec ==='1');
definePageMeta({
  layout: "page",
});
const courses = ref(coursesData.value.data)
const currentFormat = ref('All')
const courseCards = ref()

const showChatService = catalog.value.showChatService;
const experience = useExperience();
const displayJumpToTop = catalog.value.jumpToTop;
const customHeroHeader = (catalog.value && !!catalog.value.heroHeader)? catalog.value.heroHeader : null;
const overrideHeroHeader = customHeroHeader !== null;
const displayHeroHeader = catalog.value.displayHero;
const openInNewTab = catalog.value.openNewTab;
// Show/hide Ribbon Form
const showBrochureRibbon = catalog.value ? catalog.value.brochureRibbon : null;
const brochureRibbonTags = catalog.value ? catalog.value.brochureRibbonTags : [];

// Getting the filter values from the URL
const ageSelected = route.query.age && catalog.value.ageFilter.display ? getAgeRange(parseInt(route.query.age)) : ['All'];
const ageFilter = ref(ageSelected);
catalog.value.ageFilter.selected = ageSelected;

const interestsSelected = route.query.interests && catalog.value.interestFilter.display ? extractGenres(route.query.interests) : [interest.interestType];
const interestsFilter = ref(interestsSelected)
catalog.value.interestFilter.selected = interestsSelected;
if (interest.interestType !== 'All') {  
  interest.update('All')
}

const programsSelected = route.query.programs && catalog.value.programsFilter.display ? extractPrograms(route.query.programs) : ['All'];
const programsFilter = ref(programsSelected)
catalog.value.programsFilter.selected = programsSelected;

const formatSelected = route.query.format && catalog.value.formatFilter.display ? extractLearningStyles(route.query.format) : ['All'];
const formatFilter = ref(formatSelected[0])
catalog.value.formatFilter.selected = formatSelected;

catalog.value.locationFilter.selected = route.query.location && catalog.value.locationFilter.display ? route.query.location : '';

const locationFilter = ref('');

const validateFormat = (course) => {  
  if (!catalog.value.formatFilter.display) {
    return true;
  }

  return (formatFilter.value === 'On-Campus' && course.isIPC) || formatFilter.value === 'All' || (formatFilter.value === 'Online' && (!course.isIPC || (course.isIPC && course.hasOnlineCourse)))
}

const validateAge = (course) => {
  if (!catalog.value.ageFilter.display || (ageFilter.value.length == 1 && ageFilter.value[0] === 'All')) {
    return true;
  }
  else {
    const result = ageFilter.value.find((range) => {      
      const split = range.split('-');
      const min = parseInt(split[0]), max = parseInt(split[1]);
      return (parseInt(course.pulseCourse.minAge) >= min && parseInt(course.pulseCourse.minAge) <= max) || (parseInt(course.pulseCourse.maxAge) <= max && parseInt(course.pulseCourse.maxAge) >= min) || (parseInt(course.pulseCourse.minAge) <= min && parseInt(course.pulseCourse.maxAge) >= max ); 
    })
    return result;
        
  }
}

const validateInterest = (course) => {
  if (!catalog.value.interestFilter.display || (interestsFilter.value.length == 1 && interestsFilter.value[0] === 'All')) {
    return true;
  }
  else {
    // Checking if the course is OPL
    if (course.pulseCourse.division.lobTypeID === 400002 && course.pulseCourse.prodSubCategoryTypeId === 65055) {      
      if (course.genres !== null && course.genres.length === 0) {
        return false;
      }

      const matches = course.genres.filter((genre) => {
        return interestsFilter.value.includes(genre.typeString)
      })
      return matches.length > 0 ? true : false;
    }   

    const matches = course.pulseCourse.genres.filter((genre) => {
      return interestsFilter.value.includes(genre.name)
    })
    return matches.length > 0 ? true : false;
  }
}

const validatePrograms = (course) => { 
  if (!catalog.value.programsFilter.display || (programsFilter.value.length == 1 && programsFilter.value[0] === 'All')) {
    return true;
  }
  else if (programsFilter.value.includes('Tech Camps') && course.pulseCourse.division.deliveryModelTypeID === 4000 && course.pulseCourse.division.programTypeID !== 307001 && course.pulseCourse.division.lobTypeID !== 400004) {
    // Checking if the virtual equivalent is not an OPL
    if (course.relatedCourse !== null && course.relatedCourse.pulseCourse.division.lobTypeID === 400002 && course.relatedCourse.pulseCourse.prodSubCategoryTypeId === 65055 && formatFilter.value === 'Online') {
      return false;
    }
    
    return true;
  } 
  else if (programsFilter.value.includes('Teen Academies') && (course.pulseCourse.division.lobTypeID === 400004 || (course.pulseCourse.division.deliveryModelTypeID === 4000 && course.pulseCourse.division.programTypeID === 307001))) {
    return true;
  }
  else if (programsFilter.value.includes('Private Lessons') && (course.pulseCourse.division.lobTypeID === 400002 && course.pulseCourse.prodSubCategoryTypeId === 65055)) {
    return true;
  }

  return false;
}

const isVirtual = (course) => {
  return course.division && course.division.programTypeID && (course.division.programTypeID == 307006 || course.division.programTypeID == 307007);
}

const validateLocationSearch = (course) => {  
  if (locationFilter.value === '' || locationFilter.value.some((location) => course.pulseCourse.locations.includes(location) ) || (formatFilter.value === 'All' && course.isVirtual) || !catalog.value.locationFilter.display) {
    return 'MATCH';
  }
  else if (formatFilter.value === 'All' && course.relatedCourse !== null && isVirtual(course.relatedCourse.pulseCourse)) {    
    return 'ACCEPTED'
  }

  return 'REJECTED'
}

const filterSelection = () => {
  courses.value = coursesData.value.data.filter(course => {
    const locationValidation = validateLocationSearch(course);    
    const response = validateAge(course) && validateFormat(course) && validateInterest(course) && validatePrograms(course) && (locationValidation === 'MATCH' || locationValidation === 'ACCEPTED');

    return response;
  })
}

const handleFiltersChange = (filter) => {  

  if (filter.name === 'format') {
    formatFilter.value = filter.value;
    if (formatFilter.value === 'Online') {
      locationFilter.value = ''
    }    
  }

  if (filter.name === 'ages') {
    ageFilter.value = filter.value
  }

  if (filter.name === 'interests') {
    interestsFilter.value = filter.value
  }

  if (filter.name === 'programs') {
    programsFilter.value = filter.value
  }

  if (filter.name === 'location') {
    locationFilter.value = filter.value
  }
  
  filterSelection(); 
  
  if (Array.isArray(locationFilter.value) && locationFilter.value.length === 0 && formatFilter.value === 'All') {
    courseCards.value.forEach((card) => {        
      card.changeFormat('Online', false)
    })
  }
  else {
    courseCards.value.forEach((card) => {  
      card.changeFormat(formatFilter.value, locationFilter.value)
    })
  }

  scrollToFilter()
}

const getElementByValue = (genre) => {
  const container = document.getElementById('multi-interests');  
  const elements = container.querySelectorAll('input[type="checkbox"]');
  for (let i = 0; i < elements.length; i++) {
    if (elements[i].value === genre) {
      return elements[i];
    }
  }

  return null;
}

// Set meta tags in head for SEO
if (catalog.value && catalog.value.seo) {
  setPageHead(catalog.value.seo, route.path);
}

const promoBanner = ref(true)
const scrollToFilter = () => {
  const filteGrid = document.getElementById('filter-grid');

  if (filteGrid.classList.contains('sticky') && window.innerWidth >= 1200) {
    const offset = (promoBanner.value ? 103 : 68);
    window.scrollTo({
      behavior: 'smooth',
      top:
        document.getElementById("course-grid-container").parentElement.getBoundingClientRect().top -
        document.body.getBoundingClientRect().top -
        offset,
    })
  }
  
}

onMounted(() => {
  promoBanner.value = document.getElementById('promo-banner')
  watch(() => interest, () => {
    if (interest.interestType !== 'All' && interest.currentRoute === '/courses') {
      // Desktop
      const allOption = getElementByValue('All');      
      const element = getElementByValue(interest.interestType);
      // Resetting all the options
      allOption.click();
      // Setting the selected element
      element.click();      
      // Mobile
      const slug = slugify(interest.interestType);
      const allMobileCheckbox = document.getElementById(`mobile-interests-all`);
      const mobileCheckbox = document.getElementById(`mobile-interests-${slug}`);
      allMobileCheckbox.setAttribute('checked', false);
      allMobileCheckbox.click();      
      mobileCheckbox.click();

      const offset = document.getElementById('catalog-heading').offsetTop;
      window.scrollTo({
        top: offset - 70,
        behavior: 'smooth',
      });
    }

  }, {immediate: true, deep: true})  

  
  watch(isBootstrapLoaded, (loaded) => {
    if (loaded && brochureRequestStore.isUser && brochureRequestStore.isOptedOutEmail && brochureRequestStore.leadGenModalTrigger) {
      const leadGenElement = document.getElementById('lead-gen-modal');        
      const leadGenModal = bootstrap.Modal.getOrCreateInstance(leadGenElement);        
      leadGenModal.show(); 
      brochureRequestStore.setLeadGenModalTrigger(false);
    }
  })
  
})



</script>

<template>
  <LayoutHeroHeader v-if="!overrideHeroHeader && displayHeroHeader" />
  <LayoutHeroHeader :heroHeader="customHeroHeader" v-if="overrideHeroHeader && displayHeroHeader" />
  <div class="catalog-container ">
    <div class="block" id="catalog-heading">
      <div class="container">
        <div class="row" id="courses-body">
          <div class="col-sm-12" v-if="showCourseRec" v-html="catalog.courseRecContent">
            </div>            
          <div class="col-sm-12" v-html="catalog.body" v-else>            
          </div>
        </div>
        <div class="row">
          <div class="col-sm-12" id="course-grid-container">
            <CourseFilterGrid @onFiltersChanged="handleFiltersChange" :options="catalog" />
            <CourseGridMobileFilter @onFiltersChanged="handleFiltersChange" :options="catalog" :total="courses.length" />
            <p v-if="courses.length > 0" class="text-center text-xl-start ms-xl-3 mt-3 text-results">Results: <span class="fw-bold">{{ courses.length }} courses</span></p>
            <div v-if="courses.length == 0" v-html="catalog.noResultsAvailable"></div>            
          </div>
        </div>
        <div class="row courses-list row-cols-1 row-cols-sm-2 row-cols-md-2 row-cols-lg-3 row-cols-xl-4 gy-6 my-3 gx-3 ">
          <transition-group name="fade" mode="out-in">
            <div class="col" :id="`course-${course.id}`" v-for="(course, index) in courses" :key="`course-${course.id}`">
              <CourseCard :course="course"  :selection="formatFilter"  ref="courseCards" :openInNewTab="openInNewTab" :locationIds="locationFilter" /> 
            </div>
          </transition-group>
        </div>
      </div>
    </div>
    
  </div>
  
  
  <LayoutRibbonForm v-if="showBrochureRibbon" :ribbonTags="brochureRibbonTags" />
  <LayoutBackToTop v-if="displayJumpToTop"/>
  <ChatService v-if="showChatService" />
  <UIModal modalId="lead-gen-modal" modalClass="modal-dialog-centered modal-dialog-scrollable" modalTitle=""> 
    <GenericContent :data="{content : catalog.customLeadGenMessage}" />
  </UIModal>
</template>

<style lang="scss">
.courses-list {
  @include media-breakpoint-between(lg, xl) {
    justify-content: center;
    .col {
      max-width: 290px;
    }
  }

  @include media-breakpoint-between(md, lg) {
    justify-content: center;
    .col {
      max-width: 330px;
    }
  }
  
}

#course-grid-container {
  &:has(.sticky) {
    .text-results {
      margin-top: 90px !important;
    }
  }
  .location-grid-filter {
    .location-search-form {
      padding: 0px;
    }
  }
}

@keyframes smoothscroll1 {
  from, to { scroll-behavior: smooth; }
}

@keyframes smoothscroll2 {
  from, to { scroll-behavior: smooth; }
}

#catalog-heading {
  animation: smoothscroll1 1s;
}

</style>

